import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';
import { useSEO } from '../../hooks/useSEO';
import { supabase } from '../../lib/supabase';

export default function AvaliarImovelPage() {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    tipo_imovel: 'apartamento',
    endereco: '',
    cidade: 'Aracaju',
    bairro: '',
    area_total: '',
    quartos: '',
    banheiros: '',
    vagas_garagem: '',
    ano_construcao: '',
    estado_conservacao: 'bom',
    observacoes: '',
  });

  const [loading, setLoading] = useState(false);
  const [estimativa, setEstimativa] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  // SEO
  useSEO({
    title: 'Avalie seu Imóvel Gratuitamente | Mateus Oliveira - Setenove Imóveis',
    description: 'Descubra quanto vale seu imóvel em Aracaju e região. Avaliação gratuita e rápida com base no mercado imobiliário de Sergipe. WhatsApp (79) 99988-1002',
    keywords: 'avaliar imóvel aracaju, quanto vale meu imóvel, avaliação imobiliária sergipe, preço imóvel aracaju, mateus oliveira avaliação',
    canonical: window.location.origin + '/avaliar-imovel',
  });

  // Função para calcular estimativa baseada nos dados
  const calcularEstimativa = () => {
    const area = parseFloat(formData.area_total) || 0;
    const quartos = parseInt(formData.quartos) || 0;
    const banheiros = parseInt(formData.banheiros) || 0;
    const vagas = parseInt(formData.vagas_garagem) || 0;

    // Preço base por m² (varia por cidade)
    let precoM2 = 4000; // Aracaju base
    if (formData.cidade === 'Barra dos Coqueiros') precoM2 = 3500;
    if (formData.cidade === 'Nossa Senhora do Socorro') precoM2 = 3000;

    // Ajuste por tipo
    if (formData.tipo_imovel === 'casa') precoM2 *= 1.1;
    if (formData.tipo_imovel === 'terreno') precoM2 *= 0.6;
    if (formData.tipo_imovel === 'comercial') precoM2 *= 1.3;

    // Cálculo base
    let valor = area * precoM2;

    // Bônus por características
    valor += quartos * 30000;
    valor += banheiros * 20000;
    valor += vagas * 25000;

    // Ajuste por conservação
    if (formData.estado_conservacao === 'excelente') valor *= 1.15;
    if (formData.estado_conservacao === 'regular') valor *= 0.9;
    if (formData.estado_conservacao === 'reforma') valor *= 0.75;

    // Ajuste por idade
    const idade = new Date().getFullYear() - (parseInt(formData.ano_construcao) || new Date().getFullYear());
    if (idade > 20) valor *= 0.85;
    else if (idade > 10) valor *= 0.95;

    return Math.round(valor);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Calcular estimativa
      const valorEstimado = calcularEstimativa();
      setEstimativa(valorEstimado);

      // Preparar observações completas
      const observacoesCompletas = `
AVALIAÇÃO DE IMÓVEL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📍 Tipo: ${formData.tipo_imovel}
📍 Endereço: ${formData.endereco}, ${formData.bairro} - ${formData.cidade}
📐 Área Total: ${formData.area_total}m²
🛏️ Quartos: ${formData.quartos}
🚿 Banheiros: ${formData.banheiros}
🚗 Vagas: ${formData.vagas_garagem}
📅 Ano: ${formData.ano_construcao}
⭐ Conservação: ${formData.estado_conservacao}

💰 VALOR ESTIMADO: R$ ${valorEstimado.toLocaleString('pt-BR')}

📝 Observações: ${formData.observacoes || 'Nenhuma'}
      `.trim();

      // Enviar para Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit-avaliacao`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            nome: formData.nome,
            telefone: formData.telefone,
            email: formData.email,
            observacoes: observacoesCompletas,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao enviar avaliação');
      }

      console.log('Lead criado com sucesso:', result.data);
      setShowResult(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      console.error('Erro ao enviar avaliação:', error);
      
      // Mensagens de erro mais específicas
      let errorMessage = 'Erro ao enviar avaliação. ';
      
      if (error.message?.includes('Failed to fetch')) {
        errorMessage += 'Problema de conexão. Verifique sua internet e tente novamente.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Tente novamente ou entre em contato pelo WhatsApp.';
      }
      
      alert(errorMessage);
      
      // Mesmo com erro, mostrar resultado para não perder a experiência do usuário
      const valorEstimado = calcularEstimativa();
      setEstimativa(valorEstimado);
      setShowResult(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      nome: '',
      telefone: '',
      email: '',
      tipo_imovel: 'apartamento',
      endereco: '',
      cidade: 'Aracaju',
      bairro: '',
      area_total: '',
      quartos: '',
      banheiros: '',
      vagas_garagem: '',
      ano_construcao: '',
      estado_conservacao: 'bom',
      observacoes: '',
    });
    setEstimativa(null);
    setShowResult(false);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={[{ name: 'Avaliar Imóvel', url: '/avaliar-imovel' }]} />

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Avalie seu <span className="text-orange-500">Imóvel</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Descubra quanto vale seu imóvel gratuitamente. Receba uma estimativa baseada no mercado imobiliário de Sergipe.
            </p>
          </motion.div>

          {/* Resultado da Avaliação */}
          {showResult && estimativa && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 mb-8 text-white shadow-2xl"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-home-smile-line text-5xl text-white"></i>
                </div>
                <h2 className="text-3xl font-bold mb-2">Avaliação Concluída!</h2>
                <p className="text-white/90 mb-6">
                  Baseado nas informações fornecidas, estimamos o valor do seu imóvel em:
                </p>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6">
                  <p className="text-sm text-white/80 mb-2">Valor Estimado</p>
                  <p className="text-5xl font-bold">
                    R$ {estimativa.toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6">
                  <p className="text-sm text-white/90 mb-4">
                    <i className="ri-information-line mr-2"></i>
                    Esta é uma estimativa baseada em dados do mercado. Para uma avaliação precisa, entre em contato conosco!
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href={`https://wa.me/5579999881002?text=${encodeURIComponent(`Olá! Recebi a avaliação do meu imóvel no valor de R$ ${estimativa.toLocaleString('pt-BR')}. Gostaria de mais informações.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-2xl"></i>
                    Falar com Especialista
                  </a>
                  <button
                    onClick={handleReset}
                    className="px-8 py-4 bg-white/20 hover:bg-white/30 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line text-xl"></i>
                    Nova Avaliação
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Formulário */}
          {!showResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl p-8"
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dados Pessoais */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-user-line text-orange-500"></i>
                    Seus Dados
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nome Completo *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Seu nome"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.telefone}
                        onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="(79) 99999-9999"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="seu@email.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Dados do Imóvel */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-home-4-line text-orange-500"></i>
                    Dados do Imóvel
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tipo de Imóvel *
                      </label>
                      <select
                        required
                        value={formData.tipo_imovel}
                        onChange={(e) => setFormData({ ...formData, tipo_imovel: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="apartamento">Apartamento</option>
                        <option value="casa">Casa</option>
                        <option value="terreno">Terreno</option>
                        <option value="comercial">Comercial</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Cidade *
                      </label>
                      <select
                        required
                        value={formData.cidade}
                        onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="Aracaju">Aracaju</option>
                        <option value="Barra dos Coqueiros">Barra dos Coqueiros</option>
                        <option value="Nossa Senhora do Socorro">Nossa Senhora do Socorro</option>
                        <option value="São Cristóvão">São Cristóvão</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Bairro *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.bairro}
                        onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: Atalaia"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Endereço *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.endereco}
                        onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Rua, número"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Área Total (m²) *
                      </label>
                      <input
                        type="number"
                        required
                        min="1"
                        value={formData.area_total}
                        onChange={(e) => setFormData({ ...formData, area_total: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: 80"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Quartos *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={formData.quartos}
                        onChange={(e) => setFormData({ ...formData, quartos: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: 3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Banheiros *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={formData.banheiros}
                        onChange={(e) => setFormData({ ...formData, banheiros: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: 2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Vagas de Garagem *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={formData.vagas_garagem}
                        onChange={(e) => setFormData({ ...formData, vagas_garagem: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: 1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Ano de Construção *
                      </label>
                      <input
                        type="number"
                        required
                        min="1900"
                        max={new Date().getFullYear()}
                        value={formData.ano_construcao}
                        onChange={(e) => setFormData({ ...formData, ano_construcao: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: 2020"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Estado de Conservação *
                      </label>
                      <select
                        required
                        value={formData.estado_conservacao}
                        onChange={(e) => setFormData({ ...formData, estado_conservacao: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="excelente">Excelente</option>
                        <option value="bom">Bom</option>
                        <option value="regular">Regular</option>
                        <option value="reforma">Precisa Reforma</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Observações Adicionais
                      </label>
                      <textarea
                        value={formData.observacoes}
                        onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                        placeholder="Informações adicionais sobre o imóvel..."
                      ></textarea>
                    </div>
                  </div>
                </div>

                {/* Botão */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-orange-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Calculando...
                      </>
                    ) : (
                      <>
                        <i className="ri-calculator-line text-xl"></i>
                        Avaliar Meu Imóvel
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Informações */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-orange-50 border border-orange-200 rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <i className="ri-information-line text-orange-500"></i>
              Como Funciona?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-3-line text-3xl text-white"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">1. Preencha os Dados</h4>
                <p className="text-sm text-gray-600">
                  Informe as características do seu imóvel
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calculator-line text-3xl text-white"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">2. Receba a Estimativa</h4>
                <p className="text-sm text-gray-600">
                  Calculamos o valor baseado no mercado
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-message-3-line text-3xl text-white"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">3. Fale Conosco</h4>
                <p className="text-sm text-gray-600">
                  Entre em contato para avaliação detalhada
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
